'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { ColorSchemeContext } from '#pplx/frontend/aether/components/ColorSchemeProvider/useColorScheme.js';
import { removeCookie, setCookie } from '#pplx/frontend/cookies/provider.js';
import { useGetCookie } from '#pplx/frontend/cookies/useGetCookie.js';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
const INITIAL_SYSTEM_COLOR_SCHEME = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)')?.matches
        ? 'dark'
        : 'light'
    : 'light';
/**
 * Run a function when the system color scheme changes. This function should be
 * cached with useCallback().
 *
 * @param handleSystemColorSchemeChange - Callback with the new system color
 * scheme whenever it's changed.
 */
function useGetSystemColorScheme() {
    const [systemColorScheme, setSystemColorScheme] = useState(INITIAL_SYSTEM_COLOR_SCHEME);
    useEffect(() => {
        const colorSchemeMatchMedia = window.matchMedia('(prefers-color-scheme: dark)');
        const onColorSchemeChange = (ev) => {
            setSystemColorScheme(ev.matches ? 'dark' : 'light');
        };
        colorSchemeMatchMedia?.addEventListener('change', onColorSchemeChange);
        return () => {
            colorSchemeMatchMedia?.removeEventListener('change', onColorSchemeChange);
        };
    }, []);
    return systemColorScheme;
}
/**
 * The value provided through ColorSchemeContext.
 * The preference hierarchy is:
 * 1. Override color scheme (per page)
 * 2. User preferred color scheme (per user)
 * 3. System color scheme (per system)
 */
const PREFERRED_COLOR_SCHEME_COOKIE = 'colorScheme';
// Persist across sessions; a session cookie is lost on process restart (app update).
const PREFERRED_COLOR_SCHEME_COOKIE_MAX_DAYS = 400;
/**
 * Context provider for color scheme values and update functions.
 */
export const ColorSchemeProvider = React.memo(function ColorSchemeProvider({ children, initialOverrideColorScheme, }) {
    const systemColorScheme = useGetSystemColorScheme();
    const { value: userColorScheme, refetch: refetchUserColorScheme } = useGetCookie(PREFERRED_COLOR_SCHEME_COOKIE);
    const [overrideColorScheme, _setOverrideColorScheme] = useState(initialOverrideColorScheme);
    // debounce here is required to prevent multiple updates from Entropy browser
    // it deletes the cookie and then sets fresh value in the single macro-task, so 100ms is enough
    const setHtmlDatasetColorScheme = useMemo(() => debounce((cookieValue) => {
        const colorScheme = cookieValue;
        if (!colorScheme) {
            // @eslint-disable-next-line react-hooks/immutability
            delete document.documentElement.dataset.colorScheme;
        }
        else {
            // @eslint-disable-next-line react-hooks/immutability
            document.documentElement.dataset.colorScheme = colorScheme;
        }
    }, 100), []);
    const setUserPreferredColorScheme = useCallback((colorScheme) => {
        if (!colorScheme) {
            removeCookie(PREFERRED_COLOR_SCHEME_COOKIE);
        }
        else {
            setCookie(PREFERRED_COLOR_SCHEME_COOKIE, colorScheme, {
                expires: PREFERRED_COLOR_SCHEME_COOKIE_MAX_DAYS,
            });
        }
        refetchUserColorScheme();
    }, [refetchUserColorScheme]);
    const setOverrideColorScheme = useCallback((colorScheme) => {
        _setOverrideColorScheme(colorScheme);
    }, [_setOverrideColorScheme]);
    const colorScheme = overrideColorScheme ?? userColorScheme ?? systemColorScheme;
    // Sync the color scheme to the HTML dataset
    useEffect(() => {
        setHtmlDatasetColorScheme(colorScheme);
    }, [colorScheme, setHtmlDatasetColorScheme]);
    return (_jsx(ColorSchemeContext.Provider, { value: {
            colorScheme,
            isSystemColorScheme: !overrideColorScheme && !userColorScheme,
            setUserPreferredColorScheme,
            setOverrideColorScheme,
        }, children: children }));
});
//# sourceMappingURL=index.js.map